/**
 * Axis-A role-closure primitive (S0.4) — the pure `WHAT` graph half of the
 * two-axis RBAC model. Given the roles a principal seeds from and the role
 * catalog, it resolves the reflexive-transitive closure of the `inheritsFrom`
 * DAG: every role reachable by walking senior→junior inheritance edges. Zero-I/O,
 * no globals, no `Date.now`/`Math.random`, no React, no MSW — the caller supplies
 * the full catalog — so the MSW governance handlers (S1.1) and the UI guard read
 * it VERBATIM and can never drift (the same shared-selector discipline as
 * `src/lib/rbac/orgScope.ts`, `src/lib/pricing/resolvePrice.ts`,
 * `src/lib/reorder/computeReorderSuggestion.ts`).
 *
 * The model (plan §0 · Axis A — WHAT · NIST RBAC1):
 *   - A role's effective grants are the union over the reflexive-transitive
 *     closure of its `inheritsFrom` edges (senior ⊇ junior). This module returns
 *     the closed SET of role ids; `resolveEffectiveAccess` (S0.4) unions the
 *     grants over it and implication-expands.
 *   - The DAG is `inheritsFrom` — the resolver trusts the EDGES, never the
 *     seniority band (`levelId`/`levelOrder`), which is display/sort only. A
 *     `levelOrder` change can never change a closure (axis non-conflation).
 *   - Cycle-SAFE: a malformed cyclic graph (e.g. an admin fat-fingered edge)
 *     terminates via a visited-guard and returns the finite reachable set rather
 *     than looping. Cycle DETECTION for the "reject a cycle" UX (S2.3's acyclic
 *     `inheritsFrom` validation, `governance.cycleRejected`) is the separate pure
 *     `findRoleCycle` below, reused by that surface — no second implementation.
 *
 * Only the two graph fields are read (`id`, `inheritsFrom`); status / grants /
 * band live in the richer `RoleNode` (`effectivePermissions.ts`) so this stays a
 * minimal structural closure over any role-shaped catalog.
 */

/**
 * The minimal role shape the closure reads — declared structurally (not importing
 * `GovernanceRole` from the components layer) so the pure resolver never depends
 * UP on `src/components`, yet a full `GovernanceRole[]` is assignable verbatim.
 */
export interface RoleClosureNode {
  id: string;
  /**
   * Senior→junior inheritance edges (this role inherits the grants of each id).
   * Absent ⇒ a leaf role (degenerate flat-union path — identical to today).
   */
  inheritsFrom?: string[];
}

function indexById<T extends RoleClosureNode>(roles: readonly T[]): Map<string, T> {
  const byId = new Map<string, T>();
  for (const role of roles) byId.set(role.id, role);
  return byId;
}

/**
 * The reflexive-transitive closure of `seedRoleIds` over `inheritsFrom`.
 *
 * Returns the set of every role id reachable by walking inheritance edges,
 * INCLUDING each seed itself (reflexive) — but only ids that resolve to a role in
 * `roles` (an edge to an unknown id is skipped, not surfaced, so the closure is
 * always a set of real roles). Cycle-SAFE: the visited-guard terminates any cycle
 * or self-loop and makes the function idempotent
 * (`resolveRoleClosure(resolveRoleClosure(x))` equals `resolveRoleClosure(x)`).
 */
export function resolveRoleClosure(
  seedRoleIds: Iterable<string>,
  roles: readonly RoleClosureNode[]
): Set<string> {
  const byId = indexById(roles);
  const closure = new Set<string>();
  const stack: string[] = [...seedRoleIds];
  while (stack.length > 0) {
    const id = stack.pop() as string;
    if (closure.has(id)) continue; // visited-guard ⇒ cycle-safe + idempotent
    const role = byId.get(id);
    if (!role) continue; // edge to an unknown role: skip (closure = real roles only)
    closure.add(id);
    for (const parent of role.inheritsFrom ?? []) {
      if (!closure.has(parent)) stack.push(parent);
    }
  }
  return closure;
}

/**
 * The first `inheritsFrom` cycle found (as the id path that closes it, e.g.
 * `['role-a', 'role-b', 'role-a']`) or `null` when the DAG is acyclic. A pure
 * three-colour DFS — the "reject a cycle" primitive S2.3's create/edit-role
 * acyclic validation reuses (so the UI's cycle rejection and the resolver share
 * ONE definition). Independent of `resolveRoleClosure`, which is cycle-SAFE by
 * construction and never needs this to terminate.
 */
export function findRoleCycle(
  roles: readonly RoleClosureNode[]
): readonly string[] | null {
  const WHITE = 0;
  const GREY = 1;
  const BLACK = 2;
  const byId = indexById(roles);
  const color = new Map<string, number>();
  const path: string[] = [];

  function visit(id: string): readonly string[] | null {
    color.set(id, GREY);
    path.push(id);
    for (const parent of byId.get(id)?.inheritsFrom ?? []) {
      if (!byId.has(parent)) continue; // unknown edge is not part of the graph
      const c = color.get(parent) ?? WHITE;
      if (c === GREY) {
        // back-edge ⇒ cycle: return the path from `parent` back to `parent`
        return [...path.slice(path.indexOf(parent)), parent];
      }
      if (c === WHITE) {
        const found = visit(parent);
        if (found) return found;
      }
    }
    path.pop();
    color.set(id, BLACK);
    return null;
  }

  for (const role of roles) {
    if ((color.get(role.id) ?? WHITE) === WHITE) {
      const found = visit(role.id);
      if (found) return found;
    }
  }
  return null;
}
